import { QueryClient, QueryCache, MutationCache, keepPreviousData } from "@tanstack/react-query";
import { toast } from "~/lib/toast";
import { getErrorMessage, isNetworkError, isRateLimitError, getHttpStatus } from "~/lib/error-utils";
import {
  classifyServiceOutage,
  isServiceUnavailable,
  onServiceRecovered,
} from "~/lib/service-availability";

const NETWORK_TOAST_WINDOW_MS = 3000;
let lastNetworkToastAt = 0;

function showNetworkToastOnce(error: unknown) {
  const now = Date.now();
  if (now - lastNetworkToastAt < NETWORK_TOAST_WINDOW_MS) return;

  lastNetworkToastAt = now;
  toast.error(getErrorMessage(error), { id: "global-network-error" });
}

// Bug-128: generalizes the per-hook 429 treatment already applied to
// time-cards (use429Toast + placeholderData: keepPreviousData) to every
// query. Previously only time-cards kept prior data + toasted on a rate
// limit; every other list (e.g. contacts search) silently blanked out with
// no feedback when the backend throttled it.
const RATE_LIMIT_TOAST_WINDOW_MS = 3000;
let lastRateLimitToastAt = 0;

function showRateLimitToastOnce() {
  const now = Date.now();
  if (now - lastRateLimitToastAt < RATE_LIMIT_TOAST_WINDOW_MS) return;

  lastRateLimitToastAt = now;
  toast.error("Too many requests — please wait a moment before refreshing.", {
    id: "global-rate-limit-error",
  });
}

// INV-RC-17 — the bounds on how long a query waits before retrying a
// "temporarily unavailable" answer. The server says how long (the backend
// sends 1–5 s, its circuit breaker's estimate); a wild value is kept sane.
export const OUTAGE_RETRY_MIN_MS = 1_000;
export const OUTAGE_RETRY_MAX_MS = 15_000;

/**
 * Opt-out from the global error toasts below, for a query whose failure the
 * UI absorbs on purpose — a background poll that keeps showing its last good
 * answer (the platform announcement banner, OPS-6.6). Without it, a laptop
 * that drops off the restaurant WiFi gets a "No internet connection" toast
 * every five minutes from a request the user never made.
 *
 *   useQuery({ ..., meta: SILENT_QUERY_ERRORS })
 */
export const SILENT_QUERY_ERRORS = { silentErrors: true } as const;

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error, query) => {
      if (query?.meta?.silentErrors === true) return;
      if (isRateLimitError(error)) {
        showRateLimitToastOnce();
        return;
      }
      // INV-RC-17 — while the "Service temporarily unavailable — retrying"
      // banner is up, IT is the notice: a toast per failed read would only
      // repeat it, and the read re-runs on its own once the API is back.
      if (isServiceUnavailable() && classifyServiceOutage(error)) return;
      if (!isNetworkError(error)) return;

      showNetworkToastOnce(error);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Skip global toast when this mutation defines its own error handler.
      if (mutation.options.onError) return;
      if (isRateLimitError(error)) {
        showRateLimitToastOnce();
        return;
      }
      // INV-RC-17 — a write refused because the service is temporarily
      // unavailable (503 SESSION_STORE_UNAVAILABLE, a gateway 502/504) must
      // fail VISIBLY, with the server's own message ("…You are still signed
      // in — try again in a few seconds."), never silently. Writes are never
      // retried automatically: the user decides when to save again.
      if (!isNetworkError(error) && !classifyServiceOutage(error)) return;

      showNetworkToastOnce(error);
    },
  }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // Bug-128: keep the previous page/list visible while a refetch is in
      // flight — including the refetch a 429 triggers via the retry logic
      // below. Without this, every list query flips to `isLoading` on each
      // new query key / refetch and the page renders its loading skeleton
      // in place of the data the user was just looking at.
      placeholderData: keepPreviousData,
      retry: (failureCount, error) => {
        const status = getHttpStatus(error);
        // 429 (rate-limited): retry up to 2× — the throttler windows are
        // measured in seconds, so a brief back-off usually clears them. The
        // alternative (no retry) means a single load-burst leaves the user
        // stuck on the empty/error state until they manually click Retry,
        // which the e2e suite was hitting on every 5th+ serial test.
        if (status === 429) return failureCount < 2;
        // 4xx client errors (except 408 Request Timeout) shouldn't retry.
        if (status !== undefined && status >= 400 && status < 500 && status !== 408)
          return false;
        return failureCount < 1;
      },
      retryDelay: (attemptIndex, error) => {
        const status = getHttpStatus(error);
        // Throttler `Retry-After` header is in seconds; honour it when
        // present, otherwise back off ~500ms / ~1.5s / ~3s on retries.
        if (status === 429) {
          return Math.min(1000 * (attemptIndex + 1) ** 2, 3000);
        }
        // INV-RC-17 — a "temporarily unavailable" answer says when to ask
        // again; never sooner. The last data stays on screen meanwhile
        // (keepPreviousData above).
        const asked = classifyServiceOutage(error)?.retryAfterMs;
        if (asked) {
          return Math.min(Math.max(asked, OUTAGE_RETRY_MIN_MS), OUTAGE_RETRY_MAX_MS);
        }
        return Math.min(1000 * 2 ** attemptIndex, 5000);
      },
      staleTime: 10 * 60 * 1000, // 10 minutes (data is fresh for 10 mins)
      gcTime: 30 * 60 * 1000, // 30 minutes (keep in cache for 30 mins)
    },
    mutations: {
      retry: 0,
    },
  },
});

/**
 * INV-RC-17 — when the API serves again, re-run every mounted query that
 * failed during the outage, so no screen is left on its error state once the
 * banner is gone. Queries that still hold good data refresh on their own
 * schedule; only the failed ones are nudged, and a fetch already in flight
 * (the very request whose success ended the outage, say) is left to finish
 * rather than cancelled and re-sent.
 */
export function refetchFailedQueriesOnRecovery(client: QueryClient): () => void {
  return onServiceRecovered(() => {
    void client.refetchQueries(
      { type: "active", predicate: (query) => query.state.status === "error" },
      { cancelRefetch: false },
    );
  });
}

refetchFailedQueriesOnRecovery(queryClient);
